import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
} from '@angular/core';
import {
  KeyValuePipe,
  NgClass,
  NgTemplateOutlet,
  TitleCasePipe,
} from '@angular/common';
import {
  disabled,
  form,
  FormField,
  maxLength,
  required,
  schema,
  validate,
} from '@angular/forms/signals';
import { MatTooltipModule } from '@angular/material/tooltip';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import {
  WcgButtonComponent,
  WcgChipModule,
  WcgMenuOption,
  NewWcgSwitchComponent,
  ChipSize,
  TemplateTypeValidatorDirective,
} from '@wcg/frontend-framework';

import {
  ProfileCardComponent,
  ProfileCardSize,
  ProfileCardSizeEnum,
  InlineEditorComponent,
  EditModeDirective,
  ViewModeDirective,
  LabelCasePipe,
  compareObjectsFn,
  getStatusType,
} from '@erm/cluster';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';

import { ProfileInfo, DEFAULT_PROFILE_INFO } from '@data-access/profiles';

import { SuffixChoice } from '@data-access/shared';
import { MaterialItemsModule } from '@shared/material-items.module';

@Component({
  selector: 'wcg-profile-info-wrapper',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    ProfileCardComponent,
    InlineEditorComponent,
    WcgButtonComponent,
    WcgChipModule,
    EditModeDirective,
    ViewModeDirective,
    KeyValuePipe,
    NgTemplateOutlet,
    LabelCasePipe,
    FormField,
    MaterialItemsModule,
    NewWcgSwitchComponent,
    MatTooltipModule,
    NgxSkeletonLoaderComponent,
    NgClass,
    TemplateTypeValidatorDirective,
  ],
  providers: [LabelCasePipe, TitleCasePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./profile-info-wrapper.component.scss'],
  templateUrl: './profile-info-wrapper.component.html',
})
export class ProfileInfoWrapperComponent {
  readonly title = input<string>('');
  readonly cardSize = input<ProfileCardSize>(ProfileCardSizeEnum.Medium);
  readonly canEdit = input<boolean>(false);
  readonly canEditRole = input<boolean>(false);
  readonly canEditStatus = input<boolean>(false);
  readonly canEditAttribute = input<boolean>(false);
  readonly loading = input<boolean>(false);
  readonly smeqcAttributes = input<WcgMenuOption[]>([]);
  readonly roles = input<WcgMenuOption[]>([]);
  readonly isInternal = input<boolean>(false);
  readonly isStaff = input<boolean>(false);
  readonly profileInfo = input<ProfileInfo>(DEFAULT_PROFILE_INFO);
  readonly error = input<Error | null>(null);
  readonly statusList = input<WcgMenuOption[]>([]);
  readonly compareObjectsFn = compareObjectsFn;

  saveProfile = output<ProfileInfo>();

  readonly suffixes = [...Object.values(SuffixChoice)];

  getStatusType = getStatusType;
  roleChipSize = ChipSize.SLIM;

  readonly userStatusLabel = computed(() => {
    const userStatus = this._profileInfo().status;
    const label = this.statusList().find((status) => status.id === userStatus);
    return label?.label || '';
  });

  readonly isUserActive = computed(() => {
    const userStatus = this._profileInfo().status;
    return userStatus?.toLowerCase() === 'active';
  });

  /*
   * @NOTE Template Ref Annotations start
   * */
  readonly defaultSystemIdsRef!: {
    props: {
      key: string;
      value: string;
    };
  };
  /*
   * @NOTE Template Ref Annotations end
   * */

  /**
   * @NOTE Defining a signal form of profileInfo
   */

  private readonly _profileInfoSchema = schema<ProfileInfo>((schemaPath) => {
    required(schemaPath.personalDetails.firstName, {
      message: 'First Name is required',
    });
    required(schemaPath.personalDetails.lastName, {
      message: 'Last Name is required',
    });
    maxLength(schemaPath.personalDetails.firstName, 255, {
      message: 'First Name cannot exceed 255 characters',
    });
    maxLength(schemaPath.personalDetails.lastName, 255, {
      message: 'Last Name cannot exceed 255 characters',
    });
    maxLength(schemaPath.personalDetails.npi, 10, {
      message: 'NPI must be exactly 10 digits',
    });
    validate(schemaPath.personalDetails.firstName, (ctx) => {
      const _firstName = ctx.value();
      if (_firstName && _firstName.trim().length === 0) {
        return {
          kind: 'firstName',
          message: 'Should not be empty or whitespace',
        };
      }
      return null;
    });

    validate(schemaPath.personalDetails.lastName, (ctx) => {
      const _lastName = ctx.value();
      if (_lastName && _lastName.trim().length === 0) {
        return {
          kind: 'lastName',
          message: 'Should not be empty or whitespace',
        };
      }
      return null;
    });

    /*
     * Validate additionalContactIds field:
     * - Must be a comma-separated list of positive integers (e.g. "123,456")
     * - Each value must not exceed PostgreSQL INTEGER max (2,147,483,647)
     */
    validate(schemaPath.systemIds.additionalContactIds, (ctx) => {
      /* PostgresSQL INTEGER column max value */
      const MAX_INTEGER_ALLOWED = 2_147_483_647;
      /* Matches one or more digits optionally followed by comma-separated digit groups */
      const commaSeperatedPattern = /^\d+(,\d+)*$/;

      const _additionalContactIds = ctx.value();
      /** If the value is empty, it's considered valid (optional field) */

      if (!_additionalContactIds) {
        return null;
      }

      if (_additionalContactIds?.trim().length === 0) {
        return {
          kind: 'additionalContactIds',
          message: 'Additional Contact IDs cannot be empty or whitespace',
        };
      }
      if (!commaSeperatedPattern.test(_additionalContactIds.trim())) {
        /* Reject if the value doesn't match the comma-separated number format */
        return {
          kind: 'additionalContactIds',
          message:
            'Additional Contact IDs must be comma-separated numbers (e.g. 123,456)',
        };
      }

      /*
       * @NOTE: Validate duplicate Ids
       * */
      const _hasDuplicates = () => {
        const _ids = _additionalContactIds.split(',').map((id) => id.trim());
        const _uniqueIds = new Set(_ids);
        return _uniqueIds.size !== _ids.length;
      };
      if (_hasDuplicates()) {
        return {
          kind: 'additionalContactIds',
          message: 'Additional Contact IDs must not contain duplicates',
        };
      }

      /* Reject if any individual ID exceeds the PostgreSQL INTEGER max */
      const overFlowed = _additionalContactIds
        .split(',')
        .some((id) => parseInt(id, 10) >= MAX_INTEGER_ALLOWED);
      if (overFlowed) {
        return {
          kind: 'additionalContactIds',
          message: 'Additional Contact IDs must be less than 2,147,483,647',
        };
      }
      return null;
    });

    validate(schemaPath.systemIds.primaryContactId, (ctx) => {
      const _numericRegex = new RegExp(/^-?\d+(\.\d+)?$/);
      const _primaryContactId = ctx.value();
      if (!_primaryContactId) {
        return null;
      }
      /* PostgresSQL INTEGER column max value */
      const MAX_INTEGER_ALLOWED = 2_147_483_647;
      if (parseInt(_primaryContactId, 10) >= MAX_INTEGER_ALLOWED) {
        return {
          kind: 'primaryContactId',
          message: 'Primary Contact ID must be less than 2,147,483,647',
        };
      }

      if (!_numericRegex.test(_primaryContactId.trim())) {
        return {
          kind: 'primaryContactId',
          message: 'Primary Contact ID must only contain numbers',
        };
      }
      return null;
    });

    disabled(schemaPath.role.role, () => !this.canEditRole());
    disabled(schemaPath.status, () => !this.canEditStatus());
    disabled(schemaPath.role.qcFunction, () => !this.canEditAttribute());
  });

  private readonly _profileInfo = linkedSignal({
    computation: (profileInfo) => profileInfo,
    source: this.profileInfo,
  });
  readonly profileInfoForm = form(this._profileInfo, this._profileInfoSchema);
  /**
   * @NOTE signal form end
   */
  readonly editState = signal<boolean>(false);

  constructor() {
    effect(() => {
      const _error = this.error();
      if (!_error) {
        return;
      }
      untracked(() => this._resetProfileInfo());
    });
  }

  readonly originalOrder = (): number => 0;

  onStatusToggle(isActive: boolean): void {
    this.profileInfoForm.status().value.set(isActive ? 'active' : 'in_active');
  }

  saveProfileInfo(): void {
    this.saveProfile.emit(this.profileInfoForm().value());
    this.editState.set(false);
  }

  handleCancel(): void {
    this._resetProfileInfo();
  }

  private _resetProfileInfo(): void {
    this.editState.set(false);
    this._profileInfo.set(this.profileInfo());
    this.profileInfoForm().reset();
  }
}
